import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useCompetitionStore } from './competitionStore';
import type { PbnCompletion, PbnMode } from '../data/paint-by-numbers';
import type { StampLayer } from '../data/stamps/types';
import { saveArt, deleteArt, isIdbArt, artKey } from '../utils/art-db';

export interface Artwork {
  id: string;
  name: string;
  dataUrl: string;
  width: number;
  height: number;
  childId: string;
  createdAt: string;
  favorite: boolean;
  challengeId?: string;
  /**
   * If present, the artwork was created with stamp layers and can be
   * reopened in the canvas for further editing. The flat dataUrl is still
   * the canonical render for thumbnails / view-only fallback.
   */
  stampLayers?: StampLayer[];
}

export interface DrawingChallenge {
  id: string;
  title: string;
  description: string;
  emoji: string;
  difficulty: 'easy' | 'medium' | 'hard';
  category: 'freestyle' | 'copy' | 'constraint' | 'speed' | 'symmetry' | 'color-theory';
  timeLimit?: number; // seconds, if timed
  referenceEmoji?: string; // what to draw
  constraint?: string; // e.g. "only circles", "3 colors max"
  constraints?: {
    allowedTools?: Array<'brush' | 'eraser' | 'shape' | 'fill'>;
    allowedShapes?: Array<'circle' | 'rectangle' | 'line'>;
    maxColors?: number;
    palette?: string[];
    theoryTip?: string;
    autoSymmetry?: 'mirror' | 'quad' | 'six-way';
  };
  xpReward: number;
}

export const DRAWING_CHALLENGES: DrawingChallenge[] = [
  // Freestyle
  { id: 'free-1', title: 'Draw Your Pet', description: 'Draw your pet (real or imaginary!)', emoji: '🐾', difficulty: 'easy', category: 'freestyle', xpReward: 10 },
  { id: 'free-2', title: 'Dream House', description: 'Design your dream house', emoji: '🏠', difficulty: 'easy', category: 'freestyle', xpReward: 10 },
  { id: 'free-3', title: 'Alien Planet', description: 'Create a scene from an alien planet', emoji: '🪐', difficulty: 'medium', category: 'freestyle', xpReward: 15 },
  { id: 'free-4', title: 'Underwater World', description: 'Draw an underwater ocean scene', emoji: '🐠', difficulty: 'medium', category: 'freestyle', xpReward: 15 },
  { id: 'free-5', title: 'Self Portrait', description: 'Draw yourself as a cartoon character', emoji: '🧑‍🎨', difficulty: 'medium', category: 'freestyle', xpReward: 15 },

  // Speed sketches
  { id: 'speed-1', title: '30-Second Cat', description: 'Draw a cat in 30 seconds!', emoji: '🐱', difficulty: 'easy', category: 'speed', timeLimit: 30, referenceEmoji: '🐱', xpReward: 15 },
  { id: 'speed-2', title: '30-Second Tree', description: 'Draw a tree in 30 seconds!', emoji: '🌳', difficulty: 'easy', category: 'speed', timeLimit: 30, referenceEmoji: '🌳', xpReward: 15 },
  { id: 'speed-3', title: '60-Second Rocket', description: 'Draw a rocket ship in 60 seconds!', emoji: '🚀', difficulty: 'medium', category: 'speed', timeLimit: 60, referenceEmoji: '🚀', xpReward: 20 },
  { id: 'speed-4', title: '45-Second Robot', description: 'Draw a robot in 45 seconds!', emoji: '🤖', difficulty: 'medium', category: 'speed', timeLimit: 45, referenceEmoji: '🤖', xpReward: 20 },
  { id: 'speed-5', title: '30-Second Star', description: 'Draw a star as cleanly as you can in 30 seconds!', emoji: '⭐', difficulty: 'hard', category: 'speed', timeLimit: 30, referenceEmoji: '⭐', xpReward: 25 },

  // Constraint-based
  { id: 'con-1', title: 'Circles Only', description: 'Draw a face using only the circle tool', emoji: '⭕', difficulty: 'easy', category: 'constraint', constraint: 'circles only', constraints: { allowedTools: ['shape'], allowedShapes: ['circle'] }, xpReward: 15 },
  { id: 'con-2', title: 'Three Colors', description: 'Create a landscape using only 3 colors', emoji: '🎨', difficulty: 'medium', category: 'constraint', constraint: '3 colors max', constraints: { allowedTools: ['brush', 'eraser', 'fill'], maxColors: 3 }, xpReward: 20 },
  { id: 'con-3', title: 'No Eraser', description: 'Draw a flower garden — no erasing allowed!', emoji: '🌸', difficulty: 'hard', category: 'constraint', constraint: 'no eraser', constraints: { allowedTools: ['brush', 'shape', 'fill'] }, xpReward: 25 },
  { id: 'con-4', title: 'Shapes Only', description: 'Build a cityscape using only shape tools', emoji: '🏙️', difficulty: 'medium', category: 'constraint', constraint: 'shapes only', constraints: { allowedTools: ['shape'], allowedShapes: ['circle', 'rectangle', 'line'] }, xpReward: 20 },

  // Symmetry
  { id: 'sym-1', title: 'Butterfly Wings', description: 'Draw a butterfly using mirror mode', emoji: '🦋', difficulty: 'easy', category: 'symmetry', constraints: { autoSymmetry: 'mirror' }, xpReward: 15 },
  { id: 'sym-2', title: 'Mandala', description: 'Create a mandala using 4-way (four-fold) symmetry — mirror mode that repeats your strokes into four matching quadrants', emoji: '🔮', difficulty: 'medium', category: 'symmetry', constraints: { autoSymmetry: 'quad' }, xpReward: 20 },
  { id: 'sym-3', title: 'Snowflake', description: 'Design a snowflake using 6-way symmetry — real snowflakes always have six matching arms!', emoji: '❄️', difficulty: 'medium', category: 'symmetry', constraints: { autoSymmetry: 'six-way' }, xpReward: 20 },

  // Color theory
  { id: 'color-1', title: 'Warm Sunset', description: 'Paint a sunset using only warm colors (red, orange, yellow)', emoji: '🌅', difficulty: 'easy', category: 'color-theory', constraints: { palette: ['#ef4444', '#f97316', '#eab308', '#fbbf24', '#facc15', '#fb7185', '#ec4899', '#92400e'] }, xpReward: 15 },
  { id: 'color-2', title: 'Cool Ocean', description: 'Paint an ocean scene using only cool colors (blue, green, purple)', emoji: '🌊', difficulty: 'easy', category: 'color-theory', constraints: { palette: ['#2563eb', '#14b8a6', '#7c3aed'] }, xpReward: 15 },
  { id: 'color-3', title: 'Complementary Contrast', description: 'Create art using complementary color pairs (red/green, blue/orange, yellow/purple)', emoji: '🎭', difficulty: 'hard', category: 'color-theory', constraints: { palette: ['#ef4444', '#22c55e', '#2563eb', '#f97316', '#facc15', '#7c3aed'], theoryTip: 'Complementary colors sit opposite each other on the color wheel, so they make strong contrast together.' }, xpReward: 25 },
  { id: 'color-4', title: 'Monochrome Magic', description: 'Create a scene using only shades of one color', emoji: '🖤', difficulty: 'medium', category: 'color-theory', constraints: { maxColors: 4 }, xpReward: 20 },
  { id: 'color-5', title: 'Rainbow Spectrum', description: 'Paint a scene that moves through every color of the rainbow in order', emoji: '🌈', difficulty: 'medium', category: 'color-theory', constraints: { palette: ['#ef4444', '#f97316', '#facc15', '#22c55e', '#2563eb', '#7c3aed'] }, xpReward: 20 },
];

type CompletionLike = { challengeId: string };

function completedIdsByDifficulty(completions: CompletionLike[], difficulty: DrawingChallenge['difficulty']): Set<string> {
  const completed = new Set(completions.map((c) => c.challengeId));
  return new Set(
    DRAWING_CHALLENGES
      .filter((challenge) => challenge.difficulty === difficulty && completed.has(challenge.id))
      .map((challenge) => challenge.id),
  );
}

export function getUnlockedDifficulty(completions: CompletionLike[]) {
  const easyCount = completedIdsByDifficulty(completions, 'easy').size;
  const mediumCount = completedIdsByDifficulty(completions, 'medium').size;
  return {
    easy: true,
    medium: easyCount >= 3,
    hard: mediumCount >= 3,
  };
}

export function isChallengeUnlocked(challenge: DrawingChallenge, completions: CompletionLike[]): boolean {
  const unlocked = getUnlockedDifficulty(completions);
  return unlocked[challenge.difficulty];
}

function hashDate(dateStr: string): number {
  let hash = 0;
  for (let i = 0; i < dateStr.length; i += 1) {
    hash = ((hash << 5) - hash + dateStr.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

export function getDailyChallengeForDate(
  dateStr: string,
  completions: CompletionLike[] = DRAWING_CHALLENGES
    .filter((challenge) => challenge.difficulty !== 'hard')
    .map((challenge) => ({ challengeId: challenge.id })),
): DrawingChallenge {
  const eligible = DRAWING_CHALLENGES.filter(
    (challenge) => challenge.category !== 'freestyle' && isChallengeUnlocked(challenge, completions),
  );
  const pool = eligible.length > 0
    ? eligible
    : DRAWING_CHALLENGES.filter((challenge) => challenge.category !== 'freestyle' && challenge.difficulty === 'easy');
  return pool[hashDate(dateStr) % pool.length];
}

interface ChallengeCompletion {
  challengeId: string;
  childId: string;
  completedAt: string;
  artworkId: string;
  stars: 1 | 2 | 3;
}

interface ArtState {
  artworks: Artwork[];
  completedChallenges: ChallengeCompletion[];
  pbnCompletions: PbnCompletion[];

  saveArtwork: (artwork: Omit<Artwork, 'id' | 'createdAt' | 'favorite'>) => Promise<string>;
  deleteArtwork: (id: string) => void;
  toggleFavorite: (id: string) => void;
  getArtworksForChild: (childId: string) => Artwork[];

  completeChallenge: (childId: string, challengeId: string, artworkId: string, stars?: 1 | 2 | 3) => void;
  isChallengeCompleted: (childId: string, challengeId: string) => boolean;
  getCompletedCount: (childId: string) => number;
  getBestStars: (childId: string, challengeId: string) => number;

  /** Records a PBN completion. Updates the kid's best score for the picture
   *  (highest stars wins; on tie, fastest time wins). Returns the saved row. */
  recordPbnCompletion: (
    args: Omit<PbnCompletion, 'completedAt'> & { completedAt?: string }
  ) => PbnCompletion;
  /** Best run for a given child + picture, or undefined if never finished. */
  getBestPbn: (childId: string, pictureId: string) => PbnCompletion | undefined;
  /** All PBN completions for a child, newest first. */
  getPbnHistory: (childId: string) => PbnCompletion[];
  /** Set of pictureIds the child has completed at least once. */
  getCompletedPbnIds: (childId: string) => Set<string>;
}

export type { PbnCompletion, PbnMode };

export const useArtStore = create<ArtState>()(
  persist(
    (set, get) => ({
      artworks: [],
      completedChallenges: [],
      pbnCompletions: [],

      saveArtwork: async (artwork) => {
        const id = crypto.randomUUID();
        // Route the (large) base64 dataUrl into IndexedDB and store only an
        // "idb::{id}" reference in the persisted store. If IDB write fails,
        // fall back to inlining — caller still surfaces a quota error if
        // localStorage then rejects the persist write.
        let dataUrlRef = artwork.dataUrl;
        try {
          dataUrlRef = await saveArt(id, artwork.dataUrl);
        } catch (e) {
          console.warn('[artStore] IndexedDB save failed, falling back to inline dataUrl:', e);
        }
        const full: Artwork = {
          ...artwork,
          dataUrl: dataUrlRef,
          id,
          createdAt: new Date().toISOString(),
          favorite: false,
        };
        let evictedId: string | null = null;
        set((s) => {
          // Keep max 50 artworks per child to avoid runaway IDB growth
          const childArtworks = s.artworks.filter(a => a.childId === artwork.childId);
          const otherArtworks = s.artworks.filter(a => a.childId !== artwork.childId);
          const evict = childArtworks.length >= 50;
          if (evict) evictedId = childArtworks[0]?.id ?? null;
          const trimmed = evict ? childArtworks.slice(1) : childArtworks;
          return { artworks: [...otherArtworks, ...trimmed, full] };
        });
        if (evictedId) {
          deleteArt(evictedId).catch(() => { /* best-effort cleanup */ });
        }
        return id;
      },

      deleteArtwork: (id) => {
        const target = get().artworks.find(a => a.id === id);
        set((s) => ({ artworks: s.artworks.filter((a) => a.id !== id) }));
        if (target?.dataUrl && isIdbArt(target.dataUrl)) {
          deleteArt(artKey(target.dataUrl)).catch(() => { /* best-effort cleanup */ });
        }
      },

      toggleFavorite: (id) => {
        set((s) => ({
          artworks: s.artworks.map((a) =>
            a.id === id ? { ...a, favorite: !a.favorite } : a
          ),
        }));
      },

      getArtworksForChild: (childId) => {
        return get().artworks.filter((a) => a.childId === childId);
      },

      completeChallenge: (childId, challengeId, artworkId, stars = 3) => {
        const nextStars = Math.max(1, Math.min(3, stars)) as 1 | 2 | 3;

        set((s) => ({
          completedChallenges: s.completedChallenges.some(
            (completion) => completion.childId === childId && completion.challengeId === challengeId,
          )
            ? s.completedChallenges.map((completion) => {
                if (completion.childId !== childId || completion.challengeId !== challengeId) return completion;
                return {
                  ...completion,
                  artworkId,
                  completedAt: new Date().toISOString(),
                  stars: Math.max(completion.stars ?? 0, nextStars) as 1 | 2 | 3,
                };
              })
            : [
                ...s.completedChallenges,
                { childId, challengeId, artworkId, stars: nextStars, completedAt: new Date().toISOString() },
              ],
        }));

        // Submit cumulative challenge count to sibling competitions
        const newCount = get().getCompletedCount(childId);
        const comp = useCompetitionStore.getState();
        for (const evt of comp.getActiveEventsForQuiz('art-challenge')) {
          const existing = comp.results.filter(r => r.eventId === evt.id && r.childId === childId);
          const best = existing.reduce((m, r) => Math.max(m, r.score), -1);
          if (newCount > best) comp.submitResult(evt.id, childId, newCount, []);
        }
      },

      isChallengeCompleted: (childId, challengeId) => {
        return get().completedChallenges.some(
          (c) => c.childId === childId && c.challengeId === challengeId
        );
      },

      getCompletedCount: (childId) => {
        return get().completedChallenges.filter((c) => c.childId === childId).length;
      },

      getBestStars: (childId, challengeId) => {
        return get().completedChallenges
          .filter((c) => c.childId === childId && c.challengeId === challengeId)
          .reduce((best, c) => Math.max(best, c.stars ?? 0), 0);
      },

      recordPbnCompletion: (args) => {
        const row: PbnCompletion = {
          ...args,
          completedAt: args.completedAt ?? new Date().toISOString(),
        };
        set((s) => {
          // Replace if a *worse* run for the same child+picture exists; else append.
          // "Worse" = fewer stars, or equal stars but slower time.
          const existing = s.pbnCompletions.find(
            (r) => r.childId === row.childId && r.pictureId === row.pictureId
          );
          if (!existing) {
            return { pbnCompletions: [...s.pbnCompletions, row] };
          }
          const better =
            row.stars > existing.stars ||
            (row.stars === existing.stars && row.timeSec < existing.timeSec);
          if (!better) {
            // Keep the older best as the canonical row, but also retain the new run for history.
            return { pbnCompletions: [...s.pbnCompletions, row] };
          }
          return {
            pbnCompletions: s.pbnCompletions.map((r) =>
              r.childId === row.childId && r.pictureId === row.pictureId && r === existing
                ? row
                : r
            ).concat(row.completedAt !== existing.completedAt ? [row] : []),
          };
        });

        // Surface PBN completion to sibling competitions (same channel as challenges)
        const childRuns = get().pbnCompletions.filter((r) => r.childId === row.childId);
        const distinctPictures = new Set(childRuns.map((r) => r.pictureId)).size;
        const comp = useCompetitionStore.getState();
        for (const evt of comp.getActiveEventsForQuiz('art-paint-by-number')) {
          const existing = comp.results.filter(r => r.eventId === evt.id && r.childId === row.childId);
          const best = existing.reduce((m, r) => Math.max(m, r.score), -1);
          if (distinctPictures > best) comp.submitResult(evt.id, row.childId, distinctPictures, []);
        }

        return row;
      },

      getBestPbn: (childId, pictureId) => {
        const runs = get().pbnCompletions.filter(
          (r) => r.childId === childId && r.pictureId === pictureId
        );
        if (runs.length === 0) return undefined;
        return runs.reduce((best, r) => {
          if (r.stars > best.stars) return r;
          if (r.stars === best.stars && r.timeSec < best.timeSec) return r;
          return best;
        });
      },

      getPbnHistory: (childId) => {
        return get()
          .pbnCompletions.filter((r) => r.childId === childId)
          .sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());
      },

      getCompletedPbnIds: (childId) => {
        return new Set(
          get().pbnCompletions.filter((r) => r.childId === childId).map((r) => r.pictureId)
        );
      },
    }),
    { name: 'homeschool-art', version: 0, migrate: (state) => state }
  )
);
